import { getAllPackages, type BcPackage } from "@/api/backgroundCheck";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

/** The canonical shape lives with the API layer; re-exported because historical imports point here. */
export type Package = BcPackage;

interface PackagesState {
  allPackages: Package[];
  selectedPackage: Package | null; // Add selectedPackage here
  loading: boolean;
  error: string | null;
}

const initialState: PackagesState = {
  allPackages: [],
  selectedPackage: null, // Default to null
  loading: false,
  error: null,
};

// Async thunk for fetching packages
export const getPackagesAction = createAsyncThunk(
  "package/getPackages",
  async (userType: "employer" | "jobseeker", { rejectWithValue }) => {
    try {
      const response = await getAllPackages(userType);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch packages");
    }
  },
);

const packageSlice = createSlice({
  name: "package",
  initialState,
  reducers: {
    // Add a new reducer to set the selected package
    setSelectedPackage: (state, action: PayloadAction<Package>) => {
      state.selectedPackage = action.payload;
    },
    // Optional: Add a reducer to clear the selected package
    clearSelectedPackage: (state) => {
      state.selectedPackage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPackagesAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPackagesAction.fulfilled, (state, action: PayloadAction<Package[]>) => {
        state.allPackages = action.payload;
        state.loading = false;
      })
      .addCase(getPackagesAction.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload || "Something went wrong";
        state.loading = false;
      });
  },
});

export const { setSelectedPackage, clearSelectedPackage } = packageSlice.actions;

export default packageSlice.reducer;
